import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import apiClient from '../../services/apiClient.js'
import { handleApiError } from '../../utils/handleApiError.js'

// Fetch submissions by type
export const fetchSubmissions = createAsyncThunk(
    'submissions/fetchSubmissions',
    async ({ type, page = 1, limit = 20, filters = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('token')
            const config = {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                params: {
                    page,
                    limit,
                    ...filters
                }
            }

            let url = ''
            switch (type) {
                case 'newsletter':
                    url = '/submissions/newsletter'
                    break
                case 'contact':
                    url = '/submissions/contact'
                    break
                case 'enquiry':
                    url = '/submissions/enquiry'
                    break
                case 'career':
                    url = '/submissions/career'
                    break
                default:
                    throw new Error('Invalid submission type')
            }

            const response = await apiClient.get(url, config)
            return {
                type,
                data: response.data,
                page
            }
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

const submissionsSlice = createSlice({
    name: 'submissions',
    initialState: {
        // Store each type separately
        newsletter: {
            data: [],
            pagination: {},
            loading: false,
            error: null
        },
        contact: {
            data: [],
            pagination: {},
            loading: false,
            error: null
        },
        enquiry: {
            data: [],
            pagination: {},
            loading: false,
            error: null
        },
        career: {
            data: [],
            pagination: {},
            loading: false,
            error: null
        },
        // For components expecting old structure
        submissions: {
            newsletter: { data: [], pagination: {} },
            contact: { data: [], pagination: {} },
            enquiry: { data: [], pagination: {} },
            career: { data: [], pagination: {} }
        },
        loading: false,
        hasMore: {
            newsletter: false,
            contact: false,
            enquiry: false,
            career: false
        }
    },
    reducers: {
        clearSubmissions: (state) => {
            state.newsletter.data = []
            state.contact.data = []
            state.enquiry.data = []
            state.career.data = []
        },
        clearSubmissionError: (state, action) => {
            const type = action.payload
            if (type && state[type]) {
                state[type].error = null
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSubmissions.pending, (state, action) => {
                const type = action.meta.arg.type
                state[type].loading = true
                state[type].error = null
                state.loading = true
            })
            .addCase(fetchSubmissions.fulfilled, (state, action) => {
                const { type, data, page } = action.payload

                if (page === 1) {
                    // First page - replace data
                    state[type].data = data.subscriptions || data.submissions || data.applications || data
                    state.submissions[type].data = data.subscriptions || data.submissions || data.applications || data
                } else {
                    // Subsequent pages - append data
                    const newData = data.subscriptions || data.submissions || data.applications || data
                    state[type].data = [...state[type].data, ...newData]
                    state.submissions[type].data = [...state.submissions[type].data, ...newData]
                }

                // Update pagination
                state[type].pagination = data.pagination
                state.submissions[type].pagination = data.pagination

                // Update hasMore
                state.hasMore[type] = data.pagination?.hasNext || false

                state[type].loading = false
                state.loading = false
            })
            .addCase(fetchSubmissions.rejected, (state, action) => {
                const type = action.meta.arg.type
                state[type].loading = false
                state[type].error = action.payload
                state.loading = false
            })
    }
})

export const { clearSubmissions, clearSubmissionError } = submissionsSlice.actions
export default submissionsSlice.reducer